import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CAT_MAP } from "@/lib/categories";
import { ArticleCard } from "@/components/ArticleCard";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { BookOpen, ArrowRight } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/Seo";

export default function CategoryPage() {
  const { slug } = useParams();
  const cat = CAT_MAP[slug];
  const [articles, setArticles] = useState([]);
  const [guides, setGuides] = useState([]);
  const [starter, setStarter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/articles", { params: { category: slug, limit: 60 } }),
      api.get("/guides", { params: { category: slug } }),
      api.get("/starters"),
    ]).then(([a, g, s]) => {
      setArticles(a.data);
      // Solo le guide che non duplicano una «Come iniziare»: quelle hanno canonical_path.
      setGuides(g.data.filter((x) => !x.canonical_path));
      setStarter(s.data.find((x) => x.category === slug) || null);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (!cat) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center font-editorial text-2xl text-stone-800">
      <Seo title="Categoria non trovata" noindex />
      Categoria non trovata
    </div>
  );

  return (
    <div>
      <Seo
        title={`${cat.name}: notizie, valori e guide`}
        description={`${cat.description} Notizie aggiornate, fasce di prezzo indicative e consigli pratici per chi colleziona.`}
        path={`/categoria/${cat.slug}`}
        type="website"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: cat.name, path: `/categoria/${cat.slug}` }])}
      />
      <div className="relative overflow-hidden"
        style={{ background: `linear-gradient(120deg, ${cat.hex}22 0%, rgba(252,250,246,0) 60%)` }}>
        <div className="flex h-1.5 w-full" style={{ background: cat.hex }} />
        <div className="max-w-7xl mx-auto px-4 py-14 relative">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-meta text-[9px] leading-none uppercase tracking-[0.2em] text-white tape sticker-soft mb-4"
            style={{ background: cat.hex }}>
            Categoria
          </span>
          <h1 data-testid="category-title" className="font-editorial font-extrabold text-4xl sm:text-5xl tracking-tight text-stone-900">{cat.name}</h1>
          <p className="text-stone-600 mt-4 max-w-2xl">{cat.description || ""}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <DisclaimerBanner compact />

        {(starter || guides.length > 0) && (
          <section className="mt-8 rounded-2xl bg-panel border-2 border-line p-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={17} style={{ color: cat.hex }} />
              <h2 className="font-editorial font-bold text-lg text-stone-900">Guide per iniziare</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {starter && (
                <Link to={`/come-iniziare/${cat.slug}`} data-testid="starter-callout-item"
                  className="group flex items-center justify-between gap-3 p-3.5 rounded-xl bg-ink border-2 border-line hover:border-gold/35 transition-colors">
                  <span className="font-editorial font-semibold text-stone-800 group-hover:text-gold transition-colors">{starter.title}</span>
                  <ArrowRight size={16} className="text-stone-400 group-hover:text-gold shrink-0 transition-colors" />
                </Link>
              )}
              {guides.map((g) => (
                <Link key={g.id} to={`/guida/${g.slug}`} data-testid="guide-callout-item"
                  className="group flex items-center justify-between gap-3 p-3.5 rounded-xl bg-ink border-2 border-line hover:border-gold/35 transition-colors">
                  <span className="font-editorial font-semibold text-stone-800 group-hover:text-gold transition-colors">{g.title}</span>
                  <ArrowRight size={16} className="text-stone-400 group-hover:text-gold shrink-0 transition-colors" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="font-editorial font-extrabold text-2xl tracking-tight text-stone-900">Articoli</h2>
            <span className="flex-1 h-px bg-line mb-2" />
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {[...Array(6)].map((_, i) => <div key={i} className="h-72 rounded-xl bg-panel" />)}
            </div>
          ) : articles.length === 0 ? (
            <p className="text-stone-500 py-12 text-center">Nessun articolo in questa categoria per ora. La redazione sta lavorando.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
